import { array, option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { useCallback, useEffect, useMemo } from 'react';
import { Catalog } from 'modules/domains/catalog';
import { Search } from 'ui/icons';
import { BaseOption, BasicSelect } from 'ui/selects';
import { InputText } from 'ui/inputs';
import { useCatalogs, useTranslation } from '../hooks';
import { useCatalogContext } from './catalogContext';

// Alias Catalog ID type for easier reference
type CatalogId = Catalog['id'];

// (Option<Catalog>, Catalog) -> boolean
const isCatalogSelected = (currentlySelected: option.Option<Catalog>) => (current: Catalog) =>
  option.exists((selectOption: Catalog) => selectOption.id === current.id)(currentlySelected);

// (Option<Catalog>, Catalog) -> BaseOption
const mapToOption =
  (selected: option.Option<Catalog>) =>
  (catalog: Catalog): BaseOption<CatalogId> => ({
    id: `${catalog.id}`,
    value: catalog.id,
    label: catalog.name,
    isSelected: isCatalogSelected(selected)(catalog),
  });

// (CatalogId, Catalog) -> boolean
const isMatchingId = (id: CatalogId) => (catalog: Catalog) => catalog.id === id;

// ([Catalog], CatalogId) -> Maybe<Catalog>
const findEntryIn = (entries: Catalog[]) => (id: CatalogId) => entries.find(isMatchingId(id));

// ([Catalog], Option<Catalog> => void, [CatalogId]) -> Option<Catalog>
const createGetSelection =
  (catalogEntries: Catalog[]) =>
  (selected: CatalogId[]): option.Option<Catalog> =>
    pipe(
      selected, // Start with selections
      array.map(findEntryIn(catalogEntries)), // Map them to Catalog entries
      array.map(option.fromNullable), // Map them to Options
      array.compact, // Remove any Nones
      array.head, // Take the first one (if any)
    );

const getDefaultValues = (catalog: option.Option<Catalog>) =>
  pipe(
    catalog,
    option.map((val) => [val.id]),
    option.getOrElse(() => [] as CatalogId[]),
  );

export const CatalogSelector = () => {
  const { t } = useTranslation();
  const { catalog, change, hasCatalogError, updateCatalogError } = useCatalogContext();
  const { data: catalogs = [], isLoading } = useCatalogs();
  const options = useMemo(() => catalogs?.map(mapToOption(catalog)), [catalogs]);
  const getSelection = useCallback(createGetSelection(catalogs), [catalogs]);
  const defaultValues = getDefaultValues(catalog);

  const onChange = useCallback(
    (selected: CatalogId[]) => {
      const selection = getSelection(selected);
      change(selection);
    },
    [catalogs, getSelection],
  );

  useEffect(() => {
    if (option.isSome(catalog)) updateCatalogError();
  }, [catalog]);

  return (
    <BasicSelect
      options={options}
      onSelect={onChange}
      isLoading={isLoading}
      placeholder={t('catalog.selector.placeholder')}
      defaultValues={defaultValues}
      isError={hasCatalogError}
      isFullWidth
    >
      <InputText
        type="search"
        autoComplete="off"
        id="catalog-input-search"
        Prefix={<Search />}
        placeholder={t('catalog.selector.searchPlaceholder')}
        value=""
        autoFocus
      />
    </BasicSelect>
  );
};
